import type { Ref, WatchSource } from "vue";

export type PageStatus = "idle" | "pending" | "success" | "error";

export interface PaginationMeta {
  page: number;
  pages: number;
  total: number;
  size: number;
}

export interface FetchPageResult<T> {
  items: T[];
  pagination: PaginationMeta;
}

export type FetchPage<T> = (ctx: { page: number; signal: AbortSignal }) => Promise<FetchPageResult<T>>;

export interface PageDescriptor {
  page: number;
  count: number;
}

export interface PageState<T> {
  page: number;
  count: number;
  status: PageStatus;
  items: T[];
}

export function lastPageItemCount(page: number, total: number, size: number): number {
  if (size <= 0 || total <= 0) return 0;
  const pages = Math.max(1, Math.ceil(total / size));
  if (page < pages) return size;
  const remainder = total - (pages - 1) * size;
  return remainder > 0 ? remainder : size;
}

export function pageDescriptors(total: number, size: number): PageDescriptor[] {
  const pages = size > 0 && total > 0 ? Math.ceil(total / size) : 1;
  return Array.from({ length: pages }, (_, i) => ({ page: i + 1, count: lastPageItemCount(i + 1, total, size) }));
}

interface PagedListEngineOptions<T> {
  fetchPage: FetchPage<T>;
  maxConcurrent: number;
  onPageUpdate: (page: number, patch: { status: PageStatus; items?: T[] }) => void;
  onMeta: (meta: PaginationMeta, descriptors: PageDescriptor[]) => void;
  onGenerationChange: (generation: number) => void;
}

/**
 * Framework-agnostic scheduler — the single authority for page status, generation and
 * concurrency. The reactive shell (`useInfiniteList`) is a write-only projection of its callbacks,
 * so status/generation never get a second owner. Pure → unit-testable without Vue.
 */
export class PagedListEngine<T> {
  generation = 0;

  private readonly statuses = new Map<number, PageStatus>();
  private readonly requested = new Set<number>();
  private inFlight = 0;
  private controller = new AbortController();
  private started = false;
  private knownPages = 1;

  constructor(private readonly options: PagedListEngineOptions<T>) {}

  /** Page 1 loads directly, bypassing the concurrency cap. */
  start(): void {
    this.started = true;
    this.statuses.set(1, "pending");
    this.options.onPageUpdate(1, { status: "pending" });
    this.beginFetch(1);
  }

  request(page: number): void {
    if (!this.started) return; // before the first start/reset the observer must not drive a load (geo: it comes from the bbox)
    if (page > this.knownPages) return; // page count is unknown until page 1 returns — don't load ahead of it
    if ((this.statuses.get(page) ?? "idle") !== "idle") return;
    this.requested.add(page);
    this.pump();
  }

  retry(page: number): void {
    if (this.statuses.get(page) === "pending") return;
    this.statuses.set(page, "idle");
    this.request(page);
  }

  reset(): void {
    this.generation++;
    this.controller.abort();
    this.controller = new AbortController();
    this.requested.clear();
    this.inFlight = 0;
    this.statuses.clear();
    this.knownPages = 1;
    this.options.onGenerationChange(this.generation);
    this.start();
  }

  dispose(): void {
    this.controller.abort();
    this.requested.clear();
  }

  private pump(): void {
    while (this.inFlight < this.options.maxConcurrent && this.requested.size > 0) {
      const page = Math.min(...this.requested);
      this.requested.delete(page);
      if ((this.statuses.get(page) ?? "idle") !== "idle") continue;
      this.statuses.set(page, "pending");
      this.options.onPageUpdate(page, { status: "pending" });
      this.beginFetch(page);
    }
  }

  private beginFetch(page: number): void {
    this.inFlight++;
    const generation = this.generation;
    const { signal } = this.controller;
    this.options
      .fetchPage({ page, signal })
      .then((result) => {
        if (generation !== this.generation) return; // stale: a reset happened mid-flight
        this.statuses.set(page, "success");
        this.options.onPageUpdate(page, { status: "success", items: result.items });
        if (page === 1) {
          this.knownPages = Math.max(1, result.pagination.pages);
          this.options.onMeta(result.pagination, pageDescriptors(result.pagination.total, result.pagination.size));
        }
        this.inFlight--;
        this.pump();
      })
      .catch(() => {
        if (generation !== this.generation || signal.aborted) return;
        this.statuses.set(page, "error");
        this.options.onPageUpdate(page, { status: "error" });
        this.inFlight--;
        this.pump();
      });
  }
}

export interface UseInfiniteListOptions<T> {
  fetchPage: FetchPage<T>;
  /**
   * Scroll container (null = window). The engine reserves the full height of all pages up front, so an
   * element container MUST be strictly height-bounded (`overflow` + a `min-h-0` flex chain) or that height
   * inflates the layout up to the window — cf. AppSitesBrowser's `min-h-0 overflow-hidden` root.
   */
  getRoot?: () => HTMLElement | null;
  resetWhen?: WatchSource | WatchSource[];
  immediate?: boolean;
  rootMargin?: string;
  maxConcurrent?: number;
  initialPageSize?: number;
}

export function useInfiniteList<T>(options: UseInfiniteListOptions<T>) {
  const initialPageSize = options.initialPageSize ?? 10;

  const pageStates = shallowRef<PageState<T>[]>([]);
  const byPage = new Map<number, PageState<T>>();
  const generation = ref(0);
  const total = ref(0);
  const totalPages = ref(1);
  const firstPageStatus = ref<PageStatus>("pending");

  function makeState(page: number, count: number): PageState<T> {
    return reactive({ page, count, status: "idle" as PageStatus, items: [] as T[] }) as PageState<T>;
  }

  function initShell(): void {
    byPage.clear();
    const first = makeState(1, initialPageSize);
    first.status = "pending"; // show skeletons (not the empty idle state) while waiting for the first load
    byPage.set(1, first);
    pageStates.value = [first];
    total.value = 0;
    totalPages.value = 1;
    firstPageStatus.value = "pending";
  }
  initShell();

  // Soft reset: on a filter/bbox change we keep the OLD tiles on screen (the count shows "loading")
  // until the new page 1 arrives, then swap the whole list in one shot + scroll-to-top. Skeletons
  // appear only for the first load and for newly-scrolled pages — never as a transient flash on reset.
  let committedGen = -1; // generation currently displayed with real data (-1 = nothing committed yet)
  let loadingGen: number | null = null; // generation being loaded, not yet committed to the display
  let stagedItems: T[] = []; // page-1 items of the loading generation, awaiting its meta to commit

  function buildStates(descriptors: PageDescriptor[], page1Items: T[]): void {
    byPage.clear();
    pageStates.value = descriptors.map((d) => {
      const state = makeState(d.page, d.count);
      if (d.page === 1) {
        state.status = "success";
        state.items = markRaw(page1Items);
      }
      byPage.set(d.page, state);
      return state;
    });
  }

  const engine = new PagedListEngine<T>({
    fetchPage: options.fetchPage,
    maxConcurrent: options.maxConcurrent ?? 3,
    onGenerationChange: (g) => {
      loadingGen = g;
      // First load only: show the loading state. On a filter change we keep the old tiles AND the old
      // count untouched until the swap, so neither the list nor the count flashes between filter changes.
      if (committedGen < 0) {
        firstPageStatus.value = "pending";
        initShell();
      }
    },
    onPageUpdate: (page, patch) => {
      if (page === 1) {
        if (patch.status === "pending") {
          if (committedGen < 0) firstPageStatus.value = "pending"; // first load only (see onGenerationChange)
        } else if (patch.status === "success") {
          stagedItems = patch.items ?? []; // committed in onMeta
        } else if (patch.status === "error") {
          firstPageStatus.value = "error";
        }
        return;
      }
      const state = byPage.get(page);
      if (state) {
        state.status = patch.status;
        if (patch.items) state.items = markRaw(patch.items);
      }
    },
    onMeta: (meta, descriptors) => {
      // page 1 of the loading generation arrived → commit: swap the whole display at once.
      const wasCommitted = committedGen >= 0;
      total.value = meta.total;
      totalPages.value = Math.max(1, meta.pages);
      buildStates(descriptors, stagedItems);
      stagedItems = [];
      if (loadingGen != null) {
        generation.value = loadingGen; // bump the display key now → containers re-key → instant swap
        committedGen = loadingGen;
        loadingGen = null;
      }
      firstPageStatus.value = "success";
      if (wasCommitted) nextTick(scrollToListTop); // after the swap renders, so it scrolls the new layout
    },
  });

  function doReset(): void {
    engine.reset();
  }

  if (options.resetWhen) {
    const sources = Array.isArray(options.resetWhen) ? options.resetWhen : [options.resetWhen];
    if (sources.length) watch(sources, doReset, { deep: true });
  }

  const pageToEl = new Map<number, HTMLElement>();
  const elPage = new WeakMap<Element, number>();
  let observer: IntersectionObserver | null = null;
  const rootMargin = options.rootMargin ?? "100% 0px";
  const rootEl = computed(() => options.getRoot?.() ?? null);

  // The observer's `root` is fixed at construction, and the scroll element is still null during the
  // initial mount — so (re)create it in onMounted and whenever the root appears or changes (e.g. the
  // mobile sheet vs desktop aside toggle). Capturing it once would pin the wrong root (the window).
  function rebuildObserver(): void {
    observer?.disconnect();
    if (typeof IntersectionObserver === "undefined") return;
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const page = elPage.get(entry.target);
          if (page != null) engine.request(page);
        }
      },
      { root: rootEl.value, rootMargin }
    );
    for (const el of pageToEl.values()) observer.observe(el);
  }

  watch(rootEl, rebuildObserver);

  onMounted(() => {
    rebuildObserver();
    if (options.immediate !== false) engine.start();
  });

  function registerContainer(el: unknown, page: number): void {
    const previous = pageToEl.get(page);
    if (previous && previous !== el) {
      observer?.unobserve(previous);
      pageToEl.delete(page);
    }
    if (el instanceof HTMLElement) {
      pageToEl.set(page, el);
      elPage.set(el, page);
      observer?.observe(el);
    }
  }

  const topEl = shallowRef<HTMLElement | null>(null);
  function setTopRef(el: unknown): void {
    topEl.value = el instanceof HTMLElement ? el : null;
  }

  function renderedHeaderHeight(): number {
    const measured = document.querySelector("header")?.getBoundingClientRect().height;
    return measured || parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--ui-header-height")) || 0;
  }

  function listTopOffset(listTop: HTMLElement): number {
    for (let sib = listTop.nextElementSibling; sib instanceof HTMLElement; sib = sib.nextElementSibling) {
      const styles = getComputedStyle(sib);
      if (styles.position !== "sticky") continue;
      const stickyTop = parseFloat(styles.top);
      return Number.isNaN(stickyTop) ? renderedHeaderHeight() : stickyTop;
    }
    return renderedHeaderHeight();
  }

  function scrollToListTop(): void {
    const root = options.getRoot?.();
    if (root) {
      if (root.scrollTop > 0) root.scrollTo({ top: 0, behavior: "instant" });
      return;
    }
    const el = topEl.value;
    if (!el) return;
    const offset = listTopOffset(el);
    const rect = el.getBoundingClientRect();
    if (rect.top < offset) {
      window.scrollTo({ top: window.scrollY + rect.top - offset, behavior: "instant" });
    }
  }

  onScopeDispose(() => {
    engine.dispose();
    observer?.disconnect();
  });

  return {
    pageStates,
    total,
    totalPages,
    firstPageStatus,
    generation,
    registerContainer,
    setTopRef,
    loadPage: (page: number) => engine.request(page),
    retry: (page: number) => engine.retry(page),
    reset: doReset,
  };
}
